// The copy-out half of the web host's diagnostics contribution (docs/10 §1:
// "out | copy-to-clipboard | OS-owned capability", and §5 rung 5).
//
// Rung 5 is the floor of the delivery ladder and the only one that always
// works: the 2026-07-26 field incident was diagnosed from PHOTOGRAPHS of a log
// screen. So this is deliberately plain text with fixed ASCII section markers —
// readable in a chat window, on a photograph, and by a person or a model with
// no schema to hand — and character-for-character the same document the Android
// renderer produces (`android/.../ui/settings/DiagnosticsBundle.kt`, itself
// pinned to the iOS one), so one paste from any of the three reads the same way.
//
// Pure: no DOM, no clocks of its own, no logging. Nothing rendered here may
// ever be fed back into a log — a bundle carries whatever the app's console
// carried, and the clipboard is the user's own while a log is not.

import { containsResidue } from '@merkury/brain/runtime/diag/redact.js';

/** Which sections a copy carries. `BUILD` is present in every copy. */
export type DiagSection = 'BUILD' | 'BRAIN_LOG' | 'APP_LOG' | 'SNAPSHOT' | 'REPORT';

/** Everything: logs + snapshot + build info + the previewable report. */
export const EVERY_SECTION: readonly DiagSection[] = [
  'BUILD',
  'BRAIN_LOG',
  'APP_LOG',
  'SNAPSHOT',
  'REPORT',
];

export const DIAG_HEADER = '=== MERKURY DIAGNOSTICS ===';
export const DIAG_FOOTER = '=== END ===';

/**
 * What a line collapses to when it trips the residue gate. Dropped rather than
 * scrubbed, on the same reasoning as `diag/report.ts isSafeToUpload`: silently
 * mangling a leak leaves its cause in place, and a missing line is a nuisance
 * while a leaked one is a broken promise.
 */
export const REDACTED_LINE = '[redacted]';

const EMPTY = '(none)';
const NO_REPORT = '(nothing queued — the brain has no report to upload right now)';

/** One ring line, from either ring — the brain's or this host's. */
export interface BundleLogLine {
  ts: number;
  level: string;
  msg: string;
}

/**
 * What identifies the binary a report came from. Every field is a build
 * constant or a browser-owned fact; none of them is an identifier.
 */
export interface BundleBuildInfo {
  appVersion: string;
  /** The build's commit, when one was baked in. */
  appBuild?: string | undefined;
  /** `web` here; the native renderers pass `android` / `ios`. */
  platform: string;
  /** Coarse OS/browser token, e.g. `Chrome 140`. Never a full user-agent. */
  osVersion?: string | undefined;
  brainVersion?: string | undefined;
  brainCommit?: string | undefined;
  brainBuiltAt?: string | undefined;
  brainContract?: number | undefined;
  brainEntry?: string | undefined;
}

export interface BundleInput {
  capturedAtMs: number;
  build: BundleBuildInfo;
  brainLog?: readonly BundleLogLine[];
  appLog?: readonly BundleLogLine[];
  snapshotJson?: string | null;
  /**
   * The exact bytes `DiagCenter.preview()` returned — the document that would
   * be uploaded, already redacted by the brain. Never re-rendered here: a host
   * that reformats the payload is showing the user something other than what
   * would be sent (docs/10 §4).
   */
  reportBytes?: string | null;
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/**
 * `HH:mm:ss` in the zone the screen renders in, so a pasted line and a
 * photographed line say the same thing.
 */
function clockTime(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Full offset-qualified stamp, so a paste is convertible to any zone. */
function isoInstant(ms: number): string {
  const d = new Date(ms);
  const offset = -d.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return `${date}T${time}${sign}${pad(Math.floor(abs / 60))}${pad(abs % 60)}`;
}

/**
 * One rendered log line. The SCREEN draws lines with this too, so what is
 * photographed and what is pasted are the same string by construction — the two
 * have no way to drift.
 *
 * The message passes the §3 residue gate on the way out. Log hygiene is
 * enforced at the source everywhere in this repo, but a browser console is a
 * shared surface (any dependency can write to it) and this is the one place
 * where those lines become something the user copies out and forwards.
 */
export function renderLine(line: BundleLogLine): string {
  const msg = containsResidue(line.msg) ? REDACTED_LINE : line.msg;
  return `${clockTime(line.ts)} [${line.level}] ${msg}`;
}

function logBody(lines: readonly BundleLogLine[]): string {
  if (lines.length === 0) return EMPTY;
  // Newest last, matching the ring's own append order and the screen.
  return lines.map(renderLine).join('\n');
}

function brainLine(build: BundleBuildInfo): string {
  if (build.brainVersion === undefined) return 'brain (unavailable)';
  const parts = [`brain ${build.brainVersion}`];
  if (build.brainCommit !== undefined) parts.push(build.brainCommit);
  if (build.brainBuiltAt !== undefined && build.brainBuiltAt !== '') parts.push(build.brainBuiltAt);
  if (build.brainContract !== undefined) parts.push(`contract ${build.brainContract}`);
  if (build.brainEntry !== undefined) parts.push(build.brainEntry);
  return parts.join(' · ');
}

function buildBody(build: BundleBuildInfo): string {
  return [
    `app ${build.appVersion}${build.appBuild === undefined ? '' : ` (${build.appBuild})`}`,
    `platform ${build.platform}${build.osVersion === undefined ? '' : ` ${build.osVersion}`}`,
    brainLine(build),
  ].join('\n');
}

function section(title: string, body: string): string {
  return `\n--- ${title} ---\n${body}\n`;
}

/**
 * Render `sections` of `input`. `BUILD` is always included, even for a
 * single-page copy: a log with no version or platform attached is the hard half
 * of every field diagnosis, and re-asking for it costs a round trip through a
 * channel that may be exactly what is broken.
 */
export function renderBundle(
  input: BundleInput,
  sections: readonly DiagSection[] = EVERY_SECTION,
): string {
  const brainLog = input.brainLog ?? [];
  const appLog = input.appLog ?? [];
  let out = `${DIAG_HEADER}\ncaptured ${isoInstant(input.capturedAtMs)}\n`;
  out += section('BUILD', buildBody(input.build));
  if (sections.includes('BRAIN_LOG')) {
    out += section(`BRAIN LOG (${brainLog.length} lines)`, logBody(brainLog));
  }
  if (sections.includes('APP_LOG')) {
    out += section(`APP LOG (${appLog.length} lines)`, logBody(appLog));
  }
  if (sections.includes('SNAPSHOT')) {
    out += section('SNAPSHOT', input.snapshotJson || EMPTY);
  }
  if (sections.includes('REPORT')) {
    out += section('REPORT (would be uploaded)', input.reportBytes || NO_REPORT);
  }
  return `${out}\n${DIAG_FOOTER}`;
}

/** The `Copy` on one diagnostics page. */
export function renderPage(input: BundleInput, page: DiagSection): string {
  return renderBundle(input, [page]);
}
